import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { callLambda, getActionFromLambdaResults } from '../../../../helpers/lambda.js';

import NSJobComponent from '../../job.js';

import JobSchema from '../../../../models/job-schema.js';
import { isUnansweredRequest } from '../../../../ns-relationship/relationship-terms.js';
import CommercialRelationship from '../../../../models/commercial-relationship.js';
import '../../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/company/relationship-end.json';
import { jobSchema } from '../../job-schema.js';

@customElement(NSCompanyRelationshipEnd.is)
export default class NSCompanyRelationshipEnd extends NSJobComponent {
  static get is() {
    return 'ns-company-relationship-end';
  }

  @state()
  private __relationship?: CommercialRelationship;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { confirm: { type: 'hidden' }, cancel: { type: 'hidden' } } });
  }

  async connectedCallback() {
    try {
      await super.connectedCallback();

      if (!this._db) throw new Error('Unable to find db service');

      if (!this._auth) throw new Error('Unable to find auth service');

      this.__relationship = await this.loadInput('comRelationship', 'relationshipId');
    } catch (err: unknown) {
      this.failJob(err, 'Unexpected error occurred while loading the relationship');
    }
  }

  private async __confirm() {
    try {
      if (!this._auth) throw new Error('Missing auth service');
      if (!this.__relationship?.id) throw new Error('Missing required relationship');
      this.loading('Ending Relationship');
      const lambdaResult = await callLambda(
        'company/relationship-end',
        'POST',
        this._auth.token,
        JSON.stringify({ relationshipId: this.__relationship.id }),
      );

      await getActionFromLambdaResults(lambdaResult, 'commercial_relationship_ended');
      this.loaded();
      await this.outcome('confirm');
    } catch (err) {
      this.loaded();
      this.failJob(err, 'Unexpected error occurred while ending the relationship');
    }
  }

  private async __cancel() {
    this.outcome('cancel');
  }

  private get __withdrawing() {
    return !!this.__relationship && isUnansweredRequest(this.__relationship.parties, this.job.companyId ?? undefined);
  }

  render() {
    return html`
      <ns-job-layout>
        <div class="job-panel">
          <h3>
            ${this.__withdrawing ? 'Withdraw request' : 'End relationship'}: ${this.__relationship?.description ?? ''}
          </h3>
          <p>
            ${this.__withdrawing
              ? "This takes back your request before anyone has accepted it. They won't be able to accept it any more."
              : 'This will end the relationship and revoke any network access it has granted or received.'}
            This cannot be undone.
          </p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    return html`
      <wa-button variant="brand" appearance="outlined" type="button" @click=${() => this.__cancel()}>Cancel</wa-button>
      <wa-button variant="danger" type="button" @click=${() => this.__confirm()}>
        ${this.__withdrawing ? 'Withdraw request' : 'End relationship'}
      </wa-button>
    `;
  }
}
